import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { authEase, AUTH_LAUNCH_MESSAGES } from './authMotion';

interface AuthSuccessStateProps {
  title: string;
  description?: React.ReactNode;
  loadingMessage?: string;
  cinematic?: boolean;
  children?: React.ReactNode;
}

export const AuthSuccessState: React.FC<AuthSuccessStateProps> = ({
  title,
  description,
  loadingMessage,
  cinematic = false,
  children,
}) => {
  const reduceMotion = useReducedMotion();
  const [msgIndex, setMsgIndex] = useState(0);
  const messages = loadingMessage ? [loadingMessage, ...AUTH_LAUNCH_MESSAGES] : AUTH_LAUNCH_MESSAGES;

  useEffect(() => {
    if (!cinematic || !loadingMessage || reduceMotion) return undefined;
    const id = window.setInterval(
      () => setMsgIndex((i) => (i + 1) % messages.length),
      2400
    );
    return () => window.clearInterval(id);
  }, [cinematic, loadingMessage, messages.length, reduceMotion]);

  const displayMsg = cinematic && loadingMessage ? messages[msgIndex] : loadingMessage;

  return (
    <motion.div
      className="text-center"
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: authEase }}
    >
      <motion.div
        className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center"
        initial={reduceMotion ? false : { scale: 0.88, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.45, ease: authEase, delay: 0.05 }}
      >
        <motion.span
          className="absolute inset-0 rounded-full bg-emerald-400/20"
          animate={reduceMotion ? {} : { scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 2.2, repeat: Infinity }}
          aria-hidden
        />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" aria-hidden />
        </span>
      </motion.div>

      <h2 className="text-3xl font-semibold tracking-tight text-gray-900">{title}</h2>

      {description ? (
        <motion.p
          className="mt-3 text-sm leading-relaxed text-gray-500"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.35 }}
        >
          {description}
        </motion.p>
      ) : null}

      {displayMsg ? (
        <motion.p
          key={displayMsg}
          className="mt-4 text-sm text-gray-500"
          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          role="status"
          aria-live="polite"
        >
          {displayMsg}
        </motion.p>
      ) : null}

      {children ? (
        <motion.div
          className="mt-8"
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.4 }}
        >
          {children}
        </motion.div>
      ) : null}
    </motion.div>
  );
};
